import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import classNames from 'classnames'
import { SmsBlockType } from 'feature/auth/redux/authSlice'
import {
  useCallback,
  useRef,
  useState,
  ChangeEvent,
  FormEvent,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react'
import { useAppSelector } from 'store/hooks'
import { Button, TextField } from 'ui'
import { ConfirmCounter } from 'widget/SmsCodeConfirm/components/ConfirmCounter/ConfirmCounter'
import { ReqCounter } from 'widget/SmsCodeConfirm/components/ReqCounter/ReqCounter'

import BottomSection from './components/BottomSection/BottomSection'
import PersonalAgreement from './components/PersonalAgreement'
import style from './SmsCodeConfirm.module.scss'

export type AuthCodeFormProps = {
  title: string
  backHandler: () => void
  phone: string
  onCodeSubmit: (code: string, phone: string) => void
  onReRequestSms: () => void
  startTimerPosition: number
  errorText?: string | null
  onChange?: () => void
  needHide?: boolean
  isButtonLoading?: boolean
  needAgree?: boolean
  setNeedHide?: Dispatch<SetStateAction<boolean>>
}

const CODE_LENGTH = 4

// подтверждение смс-кода
export function SmsCodeConfirm({
  title,
  onCodeSubmit,
  backHandler,
  phone,
  startTimerPosition,
  onReRequestSms,
  errorText,
  onChange,
  needHide,
  needAgree,
  setNeedHide,
}: AuthCodeFormProps) {
  const [isButtonDisabled, setButtonDisabled] = useState(true)
  const inputRefs = useRef<HTMLInputElement[]>([])
  const [agreement, setAgreement] = useState(false)
  const { authInfo, smsInfo } = useAppSelector(state => state.auth)

  useEffect(() => {
    window.scrollTo(0, 0)
    return () => {
      setNeedHide?.(false)
    }
  }, [])

  const checkButtonDisabled = () => {
    setButtonDisabled(!inputRefs.current.every(ref => ref?.value.length === 1))
  }

  const handleInputChange = (
    index: number,
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    onChange?.()
    const numericValue = event.target.value.replace(/[^0-9]/g, '')
    event.target.value = numericValue

    if (numericValue && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    checkButtonDisabled()
  }

  const handleKeyDown = (
    index: number,
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    onChange?.()
    if (
      event.key === 'Backspace' &&
      index > 0 &&
      !inputRefs.current[index].value
    ) {
      event.preventDefault()
      inputRefs.current[index - 1]?.focus()
      checkButtonDisabled()
    }
  }

  const submitHandler = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      onCodeSubmit(
        inputRefs.current.reduce((val, current) => val + current.value, ''),
        phone,
      )
    },
    [onCodeSubmit, phone],
  )

  const handleReRequestSms = () => {
    onReRequestSms()
    inputRefs.current.forEach(ref => {
      if (ref) ref.value = ''
    })
  }

  useEffect(() => {
    if (!errorText) return
    inputRefs.current.forEach((ref, index) => {
      if (ref?.value) ref.value = ''
      if (index === 0) ref.focus()
    })
    checkButtonDisabled()
  }, [errorText])

  const whenZero = () => setNeedHide?.(false)
  const isCountLimitBlock = smsInfo.blockType === SmsBlockType.countLimit

  return (
    <div className={classNames(style.formWrapper, 'mt24')}>
      <form noValidate onSubmit={submitHandler}>
        <p className='h1'>{title}</p>
        {!needHide && (
          <>
            <p className='mt8'>
              Введите код из СМС, которое мы отправили на номер&nbsp;{phone}
            </p>
            <div className={classNames(style.authFormCodeInputs, 'mt24')}>
              {Array.from({ length: CODE_LENGTH }, (_, index) => (
                <TextField
                  key={index}
                  error={Boolean(errorText)}
                  autoFocus={index === 0}
                  placeholder={`${index + 1}`}
                  inputRef={el => {
                    inputRefs.current[index] = el
                  }}
                  variant='outlined'
                  type='tel'
                  required
                  autoComplete='off'
                  inputProps={{
                    maxLength: 1,
                    autoComplete: 'off',
                    'data-testid': `sms-code-input-${index}`,
                  }}
                  onChange={event => handleInputChange(index, event)}
                  onKeyDown={event => handleKeyDown(index, event)}
                />
              ))}
            </div>
            {errorText && <p className={style.errorText}>{errorText}</p>}
          </>
        )}

        {isCountLimitBlock ? (
          <ReqCounter whenZero={whenZero} />
        ) : (
          <ConfirmCounter
            onReRequestSms={handleReRequestSms}
            startTimerPosition={startTimerPosition}
            inputRefs={inputRefs}
            whenZero={whenZero}
          />
        )}

        {needAgree && (
          <PersonalAgreement
            agreement={agreement}
            setAgreement={setAgreement}
          />
        )}
        <BottomSection
          disabled={
            isButtonDisabled ||
            authInfo.isLoading ||
            Boolean(needAgree && !agreement)
          }
          isLoading={authInfo.isLoading}
        />
      </form>
      <Button
        variant='contained'
        className={style.backButton}
        onClick={backHandler}
        color='secondary'
        startIcon={<ChevronRightIcon color='primary' className='rotate180' />}
        data-testid='sms-code-back'
      >
        Назад
      </Button>
    </div>
  )
}
